import axiosInstance from './axiosInstance';

/**
 * Leave service — backs both the "Apply for leave" screen (every role) and the
 * manager-only "Leave & Approvals" screen.
 *
 * Like profileService, the endpoints are mounted twice on the API
 * (/api/leave/admin and /api/leave/manager) behind different JWT verifiers, so
 * we pick the segment from the caller's role_code in localStorage and the same
 * components work on both dashboards.
 */
const segmentForRole = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const code = user?.role?.role_code || '';
    // Managers + employees come in on the carehome-manager token verifier.
    if (code.includes('manager') || code.includes('owner') || code.includes('employee')) return 'manager';
    // admin + staffs come in through the admin token verifier.
    return 'admin';
  } catch (_) {
    return 'admin';
  }
};

const url = (suffix = '') => `/leave/${segmentForRole()}${suffix}`;

/**
 * The menu that grants approval rights. Two codes accepted because menu codes
 * have been spelled both ways in this database; the sidebar's menuCodeMap
 * carries the same pair.
 */
export const LEAVE_APPROVAL_MENU_CODES = ['leave_approval', 'leave_approvals'];

/**
 * Can this user see Leave & Approval?
 *
 * Menu-driven, exactly like every other assignable module: an admin grants the
 * "Leave & Approval" menu from the Menus screen and it appears. This is the
 * same rule the sidebar applies (menu exists in allAvailableMenus AND its id
 * is in the user's menuIds), and the API enforces it independently — this
 * only decides what the UI shows.
 *
 * Pass `allAvailableMenus` and `menuIds` straight from useAuth().
 */
export const canApproveLeave = (allAvailableMenus = [], menuIds = []) => {
  const ids = (menuIds || []).map(Number);
  return (allAvailableMenus || []).some(
    (menu) => LEAVE_APPROVAL_MENU_CODES.includes(menu?.code) && ids.includes(Number(menu.id)),
  );
};

const leaveService = {
  /** My own requests. `status` is a status name or 'all'. */
  getMyLeave: async (status = 'all') => {
    const res = await axiosInstance.get(url('/my'), { params: { status } });
    return res.data;
  },

  /**
   * Apply. Payload is one of
   *   { leave_type, duration_type: 'full_day',    start_date, end_date, notes }
   *   { leave_type, duration_type: 'hours',       start_date, start_time, end_time, notes }
   *   { leave_type, duration_type: 'hours_total', start_date, hours, notes }
   *
   * 'hours_total' is a quantity of hours that may run over more than one shift —
   * 8 hours off a 4-hour shift is two of that person's working days. No end date
   * is sent: the API derives it from the applicant's own shift length, so
   * sending one would only give it something to disagree with.
   *
   * The response carries a fresh `balance`, so the caller should use that rather
   * than re-fetching.
   */
  applyLeave: async (payload) => {
    const res = await axiosInstance.post(url('/my'), payload);
    return res.data;
  },

  /** Withdraw one of my own requests while it is still pending. */
  cancelLeave: async (id) => {
    const res = await axiosInstance.patch(url(`/my/${id}/cancel`));
    return res.data;
  },

  /**
   * My own leave totalled for a year — how much I've taken and when. Same
   * shape as the manager summary, computed by the same server code.
   */
  getMyStats: async (year) => {
    const res = await axiosInstance.get(url('/my/stats'), { params: { year } });
    return res.data;
  },

  /**
   * The shift I'm rostered to work on `date`, used to clamp the hourly-leave
   * time pickers. Shape: { hasRoster, works?, start?, end?, shiftName?, overnight? }
   */
  getShiftWindow: async (date) => {
    const res = await axiosInstance.get(url('/my/shift-window'), { params: { date } });
    return res.data;
  },

  /**
   * My annual leave allowance for a year.
   *
   * Returns every figure in BOTH days and hours, plus `unit` naming which one
   * this organisation leads with and `day_hours` (my own shift length). Never
   * convert between the two on the client — that is exactly the arithmetic the
   * server exists to own, and doing it here is how a fixed 8-hour day creeps
   * back in for somebody who works 4.
   *
   * Also returned inside getMyStats, so a screen that already fetches the
   * summary doesn't need this call as well.
   */
  getMyBalance: async (year) => {
    const res = await axiosInstance.get(url('/my/balance'), { params: { year } });
    return res.data;
  },

  // ── Manager only ──────────────────────────────────────────────────────────

  /** Every leave row in the organisation, pending first. */
  getRequests: async (params = {}) => {
    const res = await axiosInstance.get(url('/requests'), { params });
    return res.data;
  },

  /**
   * Record leave FOR a staff member (manager action). Defaults to approved —
   * a manager entering leave is deciding it, not requesting it. Pass
   * status: 'pending' to raise it for review instead.
   */
  addLeaveForEmployee: async (payload) => {
    const res = await axiosInstance.post(url('/requests'), payload);
    return res.data;
  },

  /** Staff list for the approvals screen's employee filter. */
  getEmployees: async () => {
    const res = await axiosInstance.get(url('/requests/employees'));
    return res.data;
  },

  /**
   * How much leave each staff member has taken. Omit `month` for the whole
   * year. Always returns a 12-month breakdown alongside the per-person table.
   */
  getStats: async (year, month = null) => {
    const res = await axiosInstance.get(url('/requests/stats'), {
      params: month ? { year, month } : { year },
    });
    return res.data;
  },

  /** action is 'approve' or 'reject'. */
  reviewRequest: async (id, action, reviewNote = '') => {
    const res = await axiosInstance.patch(url(`/requests/${id}`), {
      action,
      review_note: reviewNote,
    });
    return res.data;
  },

  /** Delete an approved absence, putting the person back on the rota. */
  removeRequest: async (id) => {
    const res = await axiosInstance.delete(url(`/requests/${id}`));
    return res.data;
  },

  /**
   * Every staff member's annual leave allowance for a year — entitlement, used,
   * held by pending requests, and remaining.
   *
   * A row with `is_default: true` has no stored allowance yet and is showing the
   * organisation default; it becomes a real row the first time it is edited or
   * charged. `has_roster: false` means that person's figures rest on an assumed
   * 8-hour day because no shift pattern is on file — worth flagging to the
   * manager, since it is the one case where a balance can look wrong for a
   * reason that has nothing to do with leave.
   */
  getEntitlements: async (year) => {
    const res = await axiosInstance.get(url('/requests/entitlements'), { params: { year } });
    return res.data;
  },

  /**
   * Set one person's allowance for one year — how a manager grants more than
   * the organisation default.
   *
   * The API refuses a figure below what the person has already used or booked,
   * since that would retrospectively invalidate holiday they may have taken.
   */
  updateEntitlement: async (employeeId, { year, entitlement_days, notes }) => {
    const res = await axiosInstance.patch(url(`/requests/entitlements/${employeeId}`), {
      year,
      entitlement_days,
      notes,
    });
    return res.data;
  },
};

export default leaveService;
